/* eslint-disable react/prop-types */
/* eslint-disable react/no-multi-comp */
import React, { useMemo, useState, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';

import Modal from '../components/common/modals/Modal';
import ModalBody from '../components/common/modals/ModalBody';
import ModalHeader from '../components/common/modals/ModalHeader';
import ModalFooter from '../components/common/modals/ModalFooter';
import {
    Button,
    LoadingDots,
    SearchSelect,
    Row,
    Col,
    Flex,
    FormGroup,
    FormGroupLabel,
} from '@app/ui';

import connect from '../utils/connect';
import { smvId, metricOptions } from '../constants/pacingMetrics';
import combineActions from '../utils/combineActions';
import sendNotification from '../actions/notifications/send';
import sportsApi from '../utils/sportsApi';
import getUseRightsholderAttribution from '../utils/getUseRightsholderAttribution';

import getIds from '../utils/getIdsFromSelectedObject';

import { CreatePacingContext } from './CreatePacingContext';
import MvcDatePicker from '../components/common/DatePicker/MvcDatePicker';
import { Notification } from '../components/notifications/Notifications';

type TargetHelperModalProps = {
    setValue: (value: string) => void;
    onClose: () => void;
    metric: number | undefined;
    sendNotification: (
        message: string,
        options: Notification['options'],
    ) => void;
    client: any;
    user: any;
    rightsholder: string;
    sponsor?: string;
    placements?: string;
    areAllPlacementsSelected?: boolean;
};

type Option = {
    name: string;
    value: number;
    label?: string;
};

const TargetHelperModal = ({
    setValue,
    onClose,
    metric,
    sendNotification,
    client,
    user,
    rightsholder,
    sponsor,
    placements,
    areAllPlacementsSelected,
}: TargetHelperModalProps) => {
    const { t } = useTranslation('tools');
    const creatingPacingContext = useContext(CreatePacingContext);
    const { setUserActivities } = creatingPacingContext;
    const [method, setMethod] = useState(null);
    const [inputValue, setInputValue] = useState(null);
    const [multiplier, setMultiplier] = useState(null);
    const [startMonth, setStartMonth] = useState(undefined);
    const [endMonth, setEndMonth] = useState(undefined);
    const [startDate, setStartDate] = useState(undefined);
    const [endDate, setEndDate] = useState(undefined);

    const [isLoading, setLoading] = useState(false);
    const useRightsholderAttribution = useMemo(
        () => getUseRightsholderAttribution(client, client.id),
        [client],
    );
    const metricKey = useMemo(() => {
        if (!metric) return null;
        return metricOptions.find(mo => mo.value === metric).key;
    }, [metric]);

    const methodOptions: Option[] = useMemo(() => {
        const options = [
            {
                name: t('pacing.targetHelper.methods.previousTimePeriod'),
                value: 1,
                label: t('pacing.targetHelper.labels.comparisonDateRange'),
            },
        ];
        if (metric === smvId) {
            options.unshift({
                name: t('pacing.targetHelper.methods.sponsorshipCost'),
                value: 2,
                label: t('pacing.targetHelper.labels.sponsorshipCost'),
            });
        } else {
            options.unshift({
                name: t('pacing.targetHelper.methods.contractedAgreement'),
                value: 3,
                label: t('pacing.targetHelper.labels.contractedValue'),
            });
        }
        return options;
    }, [metric, t]);

    const methodLabel = useMemo(() => {
        const methodOption = methodOptions.find(mo => mo.value === method);
        if (!methodOption) return '';
        return methodOption.label;
    }, [methodOptions, method]);

    const multipliers: Option[] | null = useMemo(() => {
        if (method === 3)
            return [
                {
                    name: '1x',
                    value: 1,
                },
                {
                    name: '1.25x',
                    value: 1.25,
                },
                {
                    name: '1.5x',
                    value: 1.5,
                },
                {
                    name: '1.75x',
                    value: 1.75,
                },
                {
                    name: '2x',
                    value: 2,
                },
            ];

        return [
            {
                name: '1x',
                value: 1,
            },
            {
                name: '1.5x',
                value: 1.5,
            },
            {
                name: '2x',
                value: 2,
            },
            {
                name: '2.5x',
                value: 2.5,
            },
            {
                name: '3x',
                value: 3,
            },
        ];
    }, [method]);

    const calculate = useCallback(async () => {
        try {
            let val = null;
            if (!method)
                throw new Error(
                    t('pacing.targetHelper.validation.provideMethod'),
                );
            if (method === 2 || method === 3) {
                if (!inputValue)
                    throw new Error(
                        t('pacing.targetHelper.validation.provideValue'),
                    );
                if (!multiplier)
                    throw new Error(
                        t('pacing.targetHelper.validation.provideMultiplier'),
                    );
                val = Math.round(parseFloat(inputValue) * multiplier);
            } else if (method === 1) {
                if (!sponsor)
                    throw new Error(
                        t('pacing.targetHelper.validation.provideSponsor'),
                    );
                setLoading(true);
                const platformsQuery = {
                    startDate,
                    endDate,
                    groupBy: ['rightsholder'],
                    orderBy: ['sponsorMediaValue-desc'],
                    displayColumns: [metricKey],
                    includeTextTypeExposures: true,
                    limit: 1,
                    rightsholderIds: [rightsholder],
                    sponsorIds: [sponsor],
                    rightsholderPlacementIds: areAllPlacementsSelected
                        ? []
                        : getIds(placements),
                    ...(useRightsholderAttribution
                        ? { useRightsholderAttribution: true }
                        : {}),
                };

                const response = (await sportsApi
                    .url('/media-value-reports')
                    .json(platformsQuery)
                    .post()
                    .json()) as { total: Record<string, number> };
                const total = response.total || {};
                val = Math.round(total[metricKey] * multiplier);
            }

            const metadata = {
                ...(inputValue && { inputValue: parseFloat(inputValue) }),
                ...(startDate && { startDate }),
                ...(endDate && { endDate }),
                multiplier: multiplier,
                method: methodLabel,
                userId: user.id,
                createdAt: new Date().toISOString(),
            };

            setUserActivities(prev => [...prev, metadata]);
            setValue(val);

            onClose();
        } catch (e) {
            sendNotification(e.message, {
                context: 'warning',
            });
        } finally {
            setLoading(false);
        }
    }, [
        method,
        methodLabel,
        inputValue,
        multiplier,
        startDate,
        endDate,
        useRightsholderAttribution,
    ]);

    const onChange = e => {
        const { value } = e.target;
        setInputValue(value);
    };

    const showCalculator = !!method;
    const isSponsorshipCost = method === 2;
    const isContractedAgreement = method === 3;
    const isPreviousPeriod = method === 1;

    return (
        <Modal className="-overflow-visible">
            <ModalHeader
                title={t('pacing.targetHelper.title')}
                onClose={onClose}
            />
            <ModalBody className="-p-3 -m-a-0 -overflow-visible">
                <Flex vertical className="gds-layout__container">
                    <Row gutter={16} className="-m-b-3">
                        <Col md={12}>
                            <p className="gds-text--body-md">
                                {t('pacing.targetHelper.instructions')}
                            </p>
                        </Col>
                        <Col md={12}>
                            <div className="-method-selector-container">
                                <SearchSelect
                                    name="goal methods"
                                    size="sm"
                                    placeholder="Goal Methods"
                                    options={methodOptions}
                                    onChange={setMethod}
                                    value={method}
                                    hideClearButton
                                />
                            </div>
                        </Col>
                    </Row>
                    {showCalculator && (
                        <Row gutter={16}>
                            <Col md={24}>
                                <p>{t('pacing.targetHelper.targetBasedOn')}</p>
                            </Col>
                            <Col md={10}>
                                <div className="gds-flex__item gds-flex__item--grow-1 gds-flex__item--shrink-0 post-filter-input">
                                    <FormGroup layout="vertical">
                                        <FormGroupLabel text={methodLabel} />
                                        <div className="gds-form-group__input-group -input-value-container">
                                            {isPreviousPeriod && (
                                                <>
                                                    <MvcDatePicker
                                                        value={startDate}
                                                        onDateSelectionChange={
                                                            setStartDate
                                                        }
                                                        inputProps={{
                                                            className:
                                                                'gds-form-group__text-input',
                                                        }}
                                                        maxDate={
                                                            endDate ||
                                                            new Date()
                                                        }
                                                        monthInCalendar={
                                                            startMonth
                                                        }
                                                        onMonthInCalendarChange={
                                                            setStartMonth
                                                        }
                                                    />
                                                    <span className="-m-h-2">
                                                        {t(
                                                            'pacing.targetHelper.dateRangeSeparator',
                                                        )}
                                                    </span>
                                                    <MvcDatePicker
                                                        value={endDate}
                                                        onDateSelectionChange={
                                                            setEndDate
                                                        }
                                                        onMonthInCalendarChange={
                                                            setEndMonth
                                                        }
                                                        monthInCalendar={
                                                            endMonth
                                                        }
                                                        inputProps={{
                                                            className:
                                                                'gds-form-group__text-input',
                                                        }}
                                                        minDate={startDate}
                                                        maxDate={new Date()}
                                                    />
                                                </>
                                            )}
                                            {(isSponsorshipCost ||
                                                isContractedAgreement) && (
                                                <>
                                                    <input
                                                        className="gds-form-group__text-input gds-form-group__text-input--sm"
                                                        type="number"
                                                        name={`input_${methodLabel}`}
                                                        onChange={onChange}
                                                        data-gds-input=""
                                                        step={'10'}
                                                        value={inputValue}
                                                    />
                                                    {isSponsorshipCost && (
                                                        <span className="-m-l-2">
                                                            {
                                                                client.currency
                                                                    .code
                                                            }
                                                        </span>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </FormGroup>
                                </div>
                            </Col>
                            <Col md={2} className="-text-align-center -m-t-4">
                                <p>X</p>
                            </Col>
                            <Col md={10}>
                                <div className="gds-flex__item gds-flex__item--grow-1 gds-flex__item--shrink-0 post-filter-input -multiplier-selector-container">
                                    <FormGroup layout="vertical">
                                        <FormGroupLabel
                                            text={t(
                                                'pacing.targetHelper.multiplier',
                                            )}
                                        />
                                        <SearchSelect
                                            name="multipliers"
                                            size="sm"
                                            placeholder="Multiplier"
                                            options={multipliers}
                                            onChange={setMultiplier}
                                            value={multiplier}
                                            hideClearButton
                                        />
                                    </FormGroup>
                                </div>
                            </Col>
                        </Row>
                    )}
                </Flex>
            </ModalBody>
            <ModalFooter>
                <Button
                    className="-float-left -m-b-2 -m-r-1"
                    size="middle"
                    variant="secondary"
                    onClick={onClose}
                >
                    {t('pacing.buttons.goBack')}
                </Button>
                <Button
                    className="-float-right -m-b-2 -m-r-1 "
                    size="middle"
                    variant="primary"
                    onClick={calculate}
                    disabled={!showCalculator}
                >
                    {t('pacing.buttons.calculate')}
                </Button>
                {isLoading && <LoadingDots />}
            </ModalFooter>
        </Modal>
    );
};

export default connect(
    ['client', 'user'],
    combineActions({
        sendNotification,
    }),
)(TargetHelperModal);
